import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../css/Login.css';
import '../css/Register.css';
import { domain } from './Domain';
import useApiRequest from './useApiRequest';
import Header from './Header';
import {
    User,
    Mail,
    Phone,
    Lock,
    Eye,
    EyeOff,
    Calendar,
    Users,
    Briefcase,
    UserPlus,
    ImagePlus,
} from 'lucide-react';

const Register = () => {
    const { postRequest } = useApiRequest();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const initial = {
        username: '',
        firstname: '',
        lastname: '',
        email: '',
        password: '',
        confirmPassword: '',
        regCategory: '',
        dob: '',
        gender: '',
        contact: '',
        profile: null,
    };
    const [formData, setFormData] = useState(initial);
    const [isPasswordMatch, setIsPasswordMatch] = useState(true);
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        const errors = validateForm(formData);
        setFormErrors(errors);
        setIsPasswordMatch(formData.password === formData.confirmPassword);
    }, [formData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleFileChange = (e) => {
        setFormData({ ...formData, profile: e.target.files[0] });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm(formData);
        if (Object.keys(errors).length === 0) {
            setLoading(true);
            const formDatas = new FormData();
            for (const key in formData) {
                if (key === 'firstname') {
                    formDatas.append('first_name', formData[key]);
                } else if (key === 'lastname') {
                    formDatas.append('last_name', formData[key]);
                } else {
                    formDatas.append(key, formData[key]);
                }
            }

            await postRequest(`${domain}/accounts/signup`, formDatas);

            setLoading(false);
            setFormData(initial);
            e.target.reset();
        } else {
            setFormErrors(errors);
        }
    };

    const validateForm = (data) => {
        const errors = {};
        for (const key in data) {
            if (data[key] === '') {
                errors[key] = `${key} is required`;
            }
        }
        if (data.password !== data.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }
        return errors;
    };

    const formValid =
        Object.keys(validateForm(formData)).length === 0 && isPasswordMatch;

    return (
        <>
            <Header />
            <div
                id="registerForm"
                className="login-page register-page animate__animated animate__fadeIn"
            >
                <div className="login-card login-card--wide">
                    <form onSubmit={handleSubmit} className="login-form" noValidate>
                        <div className="login-card-header">
                            <h1 className="login-title">Create an account</h1>
                            <p className="login-subtitle">
                                Industrial Parts — register to get started
                            </p>
                        </div>

                        <div className="register-form-body">
                            <div className="register-row register-row--split">
                                <div className="register-field">
                                    <label
                                        htmlFor="reg-firstname"
                                        className="login-label"
                                    >
                                        First name
                                    </label>
                                    <div className="login-input-wrap">
                                        <span
                                            className="login-input-icon"
                                            aria-hidden
                                        >
                                            <User size={18} strokeWidth={2} />
                                        </span>
                                        <input
                                            id="reg-firstname"
                                            type="text"
                                            name="firstname"
                                            placeholder="First name"
                                            value={formData.firstname}
                                            onChange={handleChange}
                                            className="login-input"
                                        />
                                    </div>
                                    {formErrors.firstname && (
                                        <span className="register-error">
                                            {formErrors.firstname}
                                        </span>
                                    )}
                                </div>
                                <div className="register-field">
                                    <label
                                        htmlFor="reg-lastname"
                                        className="login-label"
                                    >
                                        Last name
                                    </label>
                                    <div className="login-input-wrap">
                                        <span
                                            className="login-input-icon"
                                            aria-hidden
                                        >
                                            <User size={18} strokeWidth={2} />
                                        </span>
                                        <input
                                            id="reg-lastname"
                                            type="text"
                                            name="lastname"
                                            placeholder="Last name"
                                            value={formData.lastname}
                                            onChange={handleChange}
                                            className="login-input"
                                        />
                                    </div>
                                    {formErrors.lastname && (
                                        <span className="register-error">
                                            {formErrors.lastname}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="register-field">
                                <label
                                    htmlFor="reg-username"
                                    className="login-label"
                                >
                                    Username
                                </label>
                                <div className="login-input-wrap">
                                    <span
                                        className="login-input-icon"
                                        aria-hidden
                                    >
                                        <User size={18} strokeWidth={2} />
                                    </span>
                                    <input
                                        id="reg-username"
                                        type="text"
                                        name="username"
                                        placeholder="Username"
                                        value={formData.username}
                                        onChange={handleChange}
                                        className="login-input"
                                        autoComplete="username"
                                    />
                                </div>
                                {formErrors.username && (
                                    <span className="register-error">
                                        {formErrors.username}
                                    </span>
                                )}
                            </div>

                            <div className="register-row register-row--split">
                                <div className="register-field">
                                    <label
                                        htmlFor="reg-dob"
                                        className="login-label"
                                    >
                                        Date of birth
                                    </label>
                                    <div className="login-input-wrap">
                                        <span
                                            className="login-input-icon"
                                            aria-hidden
                                        >
                                            <Calendar size={18} strokeWidth={2} />
                                        </span>
                                        <input
                                            id="reg-dob"
                                            type="date"
                                            name="dob"
                                            onChange={handleChange}
                                            value={formData.dob}
                                            className="login-input"
                                        />
                                    </div>
                                    {formErrors.dob && (
                                        <span className="register-error">
                                            {formErrors.dob}
                                        </span>
                                    )}
                                </div>
                                <div className="register-field">
                                    <label
                                        htmlFor="reg-gender"
                                        className="login-label"
                                    >
                                        Gender
                                    </label>
                                    <div className="login-input-wrap login-input-wrap--select">
                                        <span
                                            className="login-input-icon"
                                            aria-hidden
                                        >
                                            <Users size={18} strokeWidth={2} />
                                        </span>
                                        <select
                                            id="reg-gender"
                                            name="gender"
                                            onChange={handleChange}
                                            value={formData.gender}
                                            className="login-input login-input--select"
                                        >
                                            <option value="">
                                                Select your gender
                                            </option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                        </select>
                                    </div>
                                    {formErrors.gender && (
                                        <span className="register-error">
                                            {formErrors.gender}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="register-field">
                                <label htmlFor="reg-email" className="login-label">
                                    Email
                                </label>
                                <div className="login-input-wrap">
                                    <span
                                        className="login-input-icon"
                                        aria-hidden
                                    >
                                        <Mail size={18} strokeWidth={2} />
                                    </span>
                                    <input
                                        id="reg-email"
                                        type="email"
                                        name="email"
                                        placeholder="Email address"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="login-input"
                                        autoComplete="email"
                                    />
                                </div>
                                {formErrors.email && (
                                    <span className="register-error">
                                        {formErrors.email}
                                    </span>
                                )}
                            </div>

                            <div className="register-field">
                                <label
                                    htmlFor="reg-contact"
                                    className="login-label"
                                >
                                    Phone number
                                </label>
                                <div className="login-input-wrap">
                                    <span
                                        className="login-input-icon"
                                        aria-hidden
                                    >
                                        <Phone size={18} strokeWidth={2} />
                                    </span>
                                    <input
                                        id="reg-contact"
                                        type="text"
                                        placeholder="Phone number"
                                        name="contact"
                                        onChange={handleChange}
                                        value={formData.contact}
                                        className="login-input"
                                        autoComplete="tel"
                                    />
                                </div>
                                {formErrors.contact && (
                                    <span className="register-error">
                                        {formErrors.contact}
                                    </span>
                                )}
                            </div>

                            <div className="register-row register-row--split">
                                <div className="register-field">
                                    <label
                                        htmlFor="reg-password"
                                        className="login-label"
                                    >
                                        Password
                                    </label>
                                    <div className="login-input-wrap login-input-wrap--password">
                                        <span
                                            className="login-input-icon"
                                            aria-hidden
                                        >
                                            <Lock size={18} strokeWidth={2} />
                                        </span>
                                        <input
                                            id="reg-password"
                                            type={
                                                showPassword ? 'text' : 'password'
                                            }
                                            name="password"
                                            placeholder="Password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="login-input login-input--with-toggle"
                                            autoComplete="new-password"
                                        />
                                        <button
                                            type="button"
                                            className="login-password-toggle"
                                            onClick={() =>
                                                setShowPassword((v) => !v)
                                            }
                                            aria-label={
                                                showPassword
                                                    ? 'Hide password'
                                                    : 'Show password'
                                            }
                                        >
                                            {showPassword ? (
                                                <EyeOff
                                                    size={18}
                                                    strokeWidth={2}
                                                />
                                            ) : (
                                                <Eye size={18} strokeWidth={2} />
                                            )}
                                        </button>
                                    </div>
                                    {formErrors.password && (
                                        <span className="register-error">
                                            {formErrors.password}
                                        </span>
                                    )}
                                </div>
                                <div className="register-field">
                                    <label
                                        htmlFor="reg-confirm"
                                        className="login-label"
                                    >
                                        Confirm password
                                    </label>
                                    <div className="login-input-wrap login-input-wrap--password">
                                        <span
                                            className="login-input-icon"
                                            aria-hidden
                                        >
                                            <Lock size={18} strokeWidth={2} />
                                        </span>
                                        <input
                                            id="reg-confirm"
                                            type={
                                                showConfirmPassword
                                                    ? 'text'
                                                    : 'password'
                                            }
                                            name="confirmPassword"
                                            placeholder="Confirm password"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            className="login-input login-input--with-toggle"
                                            autoComplete="new-password"
                                        />
                                        <button
                                            type="button"
                                            className="login-password-toggle"
                                            onClick={() =>
                                                setShowConfirmPassword((v) => !v)
                                            }
                                            aria-label={
                                                showConfirmPassword
                                                    ? 'Hide password'
                                                    : 'Show password'
                                            }
                                        >
                                            {showConfirmPassword ? (
                                                <EyeOff
                                                    size={18}
                                                    strokeWidth={2}
                                                />
                                            ) : (
                                                <Eye size={18} strokeWidth={2} />
                                            )}
                                        </button>
                                    </div>
                                    {formErrors.confirmPassword && (
                                        <span className="register-error">
                                            {formErrors.confirmPassword}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="register-field">
                                <label
                                    htmlFor="reg-category"
                                    className="login-label"
                                >
                                    Registration category
                                </label>
                                <div className="login-input-wrap login-input-wrap--select">
                                    <span
                                        className="login-input-icon"
                                        aria-hidden
                                    >
                                        <Briefcase size={18} strokeWidth={2} />
                                    </span>
                                    <select
                                        id="reg-category"
                                        name="regCategory"
                                        value={formData.regCategory}
                                        onChange={handleChange}
                                        className="login-input login-input--select"
                                    >
                                        <option value="" disabled>
                                            Select a category
                                        </option>
                                        <option value="User">User</option>
                                        <option value="Employee">Employee</option>
                                        <option value="Staff">Staff</option>
                                    </select>
                                </div>
                                {formErrors.regCategory && (
                                    <span className="register-error">
                                        {formErrors.regCategory}
                                    </span>
                                )}
                            </div>

                            <div className="register-field register-field--file">
                                <label
                                    htmlFor="reg-profile"
                                    className="login-label"
                                >
                                    Profile picture
                                </label>
                                <div className="register-file-row">
                                    <span
                                        className="register-file-icon"
                                        aria-hidden
                                    >
                                        <ImagePlus size={18} strokeWidth={2} />
                                    </span>
                                    <input
                                        id="reg-profile"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="register-file-input"
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="login-submit"
                                disabled={!formValid || loading}
                            >
                                <span className="login-submit-inner">
                                    <UserPlus size={18} strokeWidth={2} />
                                    {loading
                                        ? 'Submitting…'
                                        : 'Create account'}
                                </span>
                            </button>
                        </div>

                        <div className="login-register-hint">
                            <span className="login-register-text">
                                Already have an account?
                            </span>{' '}
                            <Link to="/login" className="login-link-primary">
                                Sign in
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default Register;
